<script setup>
import {useRouter} from "vue-router";
import {nextTick, onMounted, onUnmounted, ref, watch} from "vue";
import DocumentTemplateService from "../../services/DocumentTemplateService.js";
import {codePreview, formatDate} from "../../helper.js";
import FileListComponent from "../../components/file-list/FileListComponentOld.vue";
import "file-icons-js/css/style.css";
import * as fileIcons from "file-icons-js";
import GeneratedDocuments from "./GeneratedDocuments.vue";
import {createConfirmDialog} from "vuejs-confirm-dialog";
import ModalDialog from "../../components/modals/ModalDialog.vue";
import {QuillEditor} from "@vueup/vue-quill";
import Button from "../../components/forms/Button.vue";
import CreateEditDocumentTemplateModal from '@/components/modals/CreateEditDocumentTemplateModal.vue';
import {useToast} from "vue-toastification";
import FileService from '@/services/FileService';

import {useI18n} from 'vue-i18n'

const {t} = useI18n();

const toast = useToast();
const router = useRouter();
const documentTemplateId = router.currentRoute.value.params.id;

const documentTemplate = ref({});
const templateType = ref('html');
const templateTypeMap = {
  'text/html': 'html',
  'application/pdf': 'pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'word',
};

// Controls visibility of replacement upload panels (separate for word/pdf tabs)
const showReplaceWordUpload = ref(false);
const showReplacePdfUpload = ref(false);

// Whether to keep existing PDF annotation layer (form fields, signatures) when replacing the PDF background
const keepPdfAnnotations = ref(true);

const quill = ref(null);
// HTML obsah čekající na naplnění do Quill editoru — nastavuje se když quill ještě není
// namountován (např. při prvním načtení stránky kdy QuillEditor čeká za v-if).
const pendingQuillHtml = ref(null);

// Jakmile se QuillEditor namountuje a quill ref se nastaví, naplníme čekající HTML.
watch(quill, (q) => {
  if (q && pendingQuillHtml.value !== null) {
    q.setHTML(pendingQuillHtml.value);
    pendingQuillHtml.value = null;
  }
});
const quillOptions = {
  placeholder: t('documentTemplateDetail.insert_template_pattern'),
  modules: {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      ['blockquote', 'code-block'],
      [{header: 1}, {header: 2}],
      [{list: 'ordered'}, {list: 'bullet'}],
      [{script: 'sub'}, {script: 'super'}],
      [{indent: '-1'}, {indent: '+1'}],
      [{size: ['small', false, 'large', 'huge']}],
      [{header: [1, 2, 3, 4, 5, 6, false]}],
      [{color: []}, {background: []}],
      [{font: []}],
      [{align: []}],
      ['clean'],
    ]
  }
};

function fetchDocumentTemplate() {
  DocumentTemplateService.getDocumentTemplate(documentTemplateId, 'extend').then((response) => {
    documentTemplate.value = response;
    if (documentTemplate.value.mime_type === 'text/html') {
      if (quill.value) {
        // Quill je již namountován — nastavíme HTML po dalším renderu.
        nextTick(() => quill.value?.setHTML(documentTemplate.value.template));
      } else {
        // Quill ještě není namountován (QuillEditor je za v-if který se teprve vyhodnotí).
        // Obsah uložíme a watcher na quill ref ho naplní jakmile se editor namountuje.
        pendingQuillHtml.value = documentTemplate.value.template;
      }
    }
    // template type should be word, pdf, html by mime type
    templateType.value = templateTypeMap[documentTemplate.value.mime_type] ?? 'html';
  });
}

function removeTemplate() {
  const {reveal, onConfirm, onCancel} = createConfirmDialog(ModalDialog, {
    title: t('documentTemplateDetail.removing_template_pattern'),
    message: t('documentTemplateDetail.removing_template_pattern_confirm'),
  });
  reveal();
  onConfirm(() => {
    DocumentTemplateService.removeTemplateFile(documentTemplateId).then(() => {
      fetchDocumentTemplate();
    });
  });
  onCancel(() => {
  });
}

function saveDocumentTemplateHtml() {
  DocumentTemplateService.saveDocumentTemplateHTML(documentTemplateId, quill.value.getHTML()).then(() => {
    fetchDocumentTemplate();
  });
}

function editDocumentTemplate() {
  const {reveal, onConfirm, onCancel} = createConfirmDialog(CreateEditDocumentTemplateModal, {documentTemplate: documentTemplate.value});
  reveal();
  onConfirm((data) => {
    DocumentTemplateService.saveDocumentTemplate(data, documentTemplate.value.id).then(response => {
      toast.success(response.message);
      fetchDocumentTemplate();
    });
  });
  onCancel(() => {
  });
}

function onFileUploaded() {
  // Warn when replacing a template that already has generated documents
  if (documentTemplate.value.generated_count > 0) {
    toast.warning(t('documentTemplateDetail.template_replaced_regenerate_warning'));
  }
  showReplaceWordUpload.value = false;
  showReplacePdfUpload.value = false;
  fetchDocumentTemplate();
}

function onRefetch() {
  fetchDocumentTemplate();
}

function downloadTemplate(extension) {
  let base64String = documentTemplate.value.template;
  if (extension === 'pdf') {
    base64String = 'application/pdf;base64,' + base64String;
  }
  if (extension === 'docx') {
    base64String = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document;base64,' + base64String;
  }
  FileService.downloadFileByPlatform(base64String, documentTemplate.value.template_name, extension);
}

onMounted(() => {
  fetchDocumentTemplate();
  // Reagujeme na úspěšné nahrání souboru z dropzone komponenty
  emitter.on('file-component:uploaded', onFileUploaded);
  // Reagujeme na žádost o znovunačtení šablony (např. po uložení HTML)
  emitter.on('document-template:refetch', onRefetch);
});

onUnmounted(() => {
  // Odstraníme listenery aby nedocházelo k akumulaci při navigaci tam a zpět
  emitter.off('file-component:uploaded', onFileUploaded);
  emitter.off('document-template:refetch', onRefetch);
});
</script>

<template>
  <div v-if="documentTemplate.id" class="page-section">
    <div class="page-section__heading">
      <div class="row">
        <div class="col-4">
          <a @click="router.back()" class="page-section__heading__back"><i class="mdi mdi-arrow-left"></i> {{ t('general.back') }}</a>
        </div>
        <div class="col-4 text-center">
          <div class="page-section__title">
            {{ t('documentTemplateDetail.template_detail') }}
          </div>
        </div>
        <div class="col-4 d-flex justify-content-end">
        </div>
      </div>
    </div>
    <div class="page-section__content">
      <div class="mb-4">
        <div class="row">

          <div class="col-12 col-md-6">
            <div class="card">
              <div class="card-header">
                <div class="row">
                  <div class="col">
                    <div class="card-header__title">
                      {{ t('documentTemplateDetail.basic_info') }}
                    </div>
                  </div>
                  <div class="col text-end">
                    <div class="card-header__action">
                      <Button label="Upravit" @click="editDocumentTemplate()"/>
                    </div>
                  </div>
                </div>
              </div>
              <div class="card-body">
                <div class="info-table">



                    <div class="info-table__row row">
                      <span class="info-table__label col-12">{{ t('documentTemplateDetail.title') }}:</span>
                      <span class="info-table__value col-12">{{ documentTemplate.name }}</span>
                    </div>
                    <div class="info-table__row row">
                      <span class="info-table__label col-12">{{ t('documentTemplateDetail.created_at') }}:</span>
                      <span class="info-table__value col-12">{{ formatDate(documentTemplate.created_at, 'j.n.Y') }}</span>
                    </div>
                    <div class="info-table__row row">
                      <span class="info-table__label col-12">{{ t('documentTemplateDetail.numbering_format') }}:</span>
                      <span class="info-table__value col-12">{{ codePreview(documentTemplate.code_format) }}</span>
                    </div>
                  <div class="info-table__row row">
                    <span class="info-table__label col-12">{{ t('documentTemplateDetail.description') }}:</span>
                    <span class="info-table__value col-12">{{ documentTemplate.description }}</span>
                  </div>
                  <div class="info-table__row row">
                    <span class="info-table__label col-12">{{ t('documentTemplateDetail.category') }}:</span>
                    <span class="info-table__value col-12">
                      <span class="badge bg-secondary me-1" v-for="category of documentTemplate.categories">{{ category.name }}</span>
                    </span>
                  </div>
                  <div class="info-table__row row">
                    <span class="info-table__label col-12">{{ t('documentTemplateDetail.active') }}:</span>
                    <span class="info-table__value col-12">{{ documentTemplate.is_active ? t('documentTemplateDetail.yes') : t('documentTemplateDetail.no') }}</span>
                  </div>
                  <div class="info-table__row row">
                    <span class="info-table__label col-12">{{ t('documentTemplateDetail.intermediaries') }}:</span>
                    <span class="info-table__value col-12">
                      <span class="d-block">{{ documentTemplate.intermediary_ids === null ? t('documentTemplateDetail.intermediaries_all') : t('documentTemplateDetail.intermediaries_specific') }}</span>

                      <span class="badge bg-danger me-1" v-if="documentTemplate.intermediary_ids && !documentTemplate.intermediaries.length">{{ t('documentTemplateDetail.no_intemediaries_selected') }}</span>
                      <span v-if="documentTemplate.intermediary_ids" class="badge bg-secondary me-1" v-for="intermediary of documentTemplate.intermediaries">{{ intermediary.label }}</span>

                    </span>
                  </div>



                  <div class="info-table__row row">
                    <span class="info-table__label col-12">{{ t('documentTemplateDetail.require_signature') }}:</span>
                    <span class="info-table__value col-12">
                      <span>{{ documentTemplate.signable ? t('documentTemplateDetail.yes') : t('documentTemplateDetail.no') }}</span>
                      <span v-if="documentTemplate.require_owner_signature || documentTemplate.require_responsible_signature">, </span>
                      <span v-if="documentTemplate.require_owner_signature">{{ t('documentTemplateDetail.by_client') }}</span>
                      <span v-if="documentTemplate.require_owner_signature && documentTemplate.require_responsible_signature"> {{ t('documentTemplateDetail.a') }} </span>
                      <span v-if="documentTemplate.require_responsible_signature">{{ t('documentTemplateDetail.by_responsible_person') }}</span>
                    </span>
                  </div>


                </div>
              </div>
            </div>
          </div>

          <div class="col-12 col-md-6">
            <div class="row g-3 mt-0">
              <div class="col-lg-6 mt-0">
                <div class="card shadow bg-light">
                  <div class="card-body">
                    <h5 class="card-title">{{ t('documentTemplateDetail.generated') }}</h5>
                    {{ documentTemplate.generated_count }}
                  </div>
                </div>
              </div>
              <div class="col-lg-6 mt-3 mt-lg-0">
                <div class="card shadow bg-light">
                  <div class="card-body">
                    <h5 class="card-title">{{ t('documentTemplateDetail.signed2') }}</h5>
                    {{ documentTemplate.signed_count }}
                  </div>
                </div>
              </div>
              <div class="col-lg-6">
                <div class="card shadow bg-light">
                  <div class="card-body">
                    <h5 class="card-title">{{ t('documentTemplateDetail.to_signed') }}</h5>
                    {{ documentTemplate.to_be_signed_count }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="page-section__heading d-flex justify-content-between align-items-center mb-0">
      <div class="page-section__title">
        {{ t('documentTemplateDetail.sample2') }}
      </div>
      <div class="btn-group">
        <button @click="templateType = 'word'" class="btn btn-outline-primary btn-primary" :class="{'active': templateType === 'word'}" :disabled="documentTemplate.generated_count > 0 && templateType !== 'word'">{{ t('documentTemplateDetail.word') }}</button>
        <button @click="templateType = 'pdf'" class="btn btn-outline-primary btn-primary" :class="{'active': templateType === 'pdf'}" :disabled="documentTemplate.generated_count > 0 && templateType !== 'pdf'">{{ t('documentTemplateDetail.pdf') }}</button>
        <button @click="templateType = 'html'" class="btn btn-outline-primary btn-primary" :class="{'active': templateType === 'html'}" :disabled="documentTemplate.generated_count > 0 && templateType !== 'html'">{{ t('documentTemplateDetail.editor') }}</button>
      </div>
    </div>
    <div class="page-section__content">
      <div v-if="Object.keys(documentTemplate).length" class="my-4">
        <div class="row mt-3">
          <div class="col-12">
            <div class="card rounded-lg shadow">
            <div v-show="templateType === 'word'" class="card-body">
              <!-- Warning: existing generated documents will need to be regenerated after template change -->
              <div v-if="documentTemplate.generated_count > 0" class="alert alert-warning mb-3">
                <i class="mdi mdi-alert-circle-outline me-1"></i>
                {{ t('documentTemplateDetail.template_upload_with_docs_warning') }}
              </div>

              <!-- Upload area: shown when no Word template is attached yet -->
              <FileListComponent
                v-if="!documentTemplate.template_name || documentTemplate.mime_type !== 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'"
                :single-file="true"
                accepted-files=".docx"
                :show-file-preview="false"
                :fileable="'App\\Models\\DocumentTemplate'"
                :fileable-id="documentTemplate.id"
              />

              <!-- File info: shown when a Word template is already attached -->
              <div v-else>
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <span :class="fileIcons.getClassWithColor(documentTemplate.template_name)" class="me-1"></span>
                    {{ documentTemplate.template_name }}
                  </div>
                  <div class="d-flex gap-3 align-items-center">
                    <router-link v-if="documentTemplate.mime_type !== 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'" :to="'/documents/templates/' + documentTemplate.id + '/preview'" class="mdi mdi-eye text-primary cursor-pointer fs-4"></router-link>
                    <span @click="downloadTemplate('docx')" class="mdi mdi-download text-primary cursor-pointer fs-4"></span>
                    <!-- Delete only allowed when no documents have been generated yet -->
                    <span v-if="documentTemplate.generated_count < 1" @click="removeTemplate()" class="mdi mdi-trash-can text-danger cursor-pointer fs-4"></span>
                    <span v-else class="mdi mdi-trash-can text-danger opacity-50 fs-4"></span>
                    <!-- Replace button: toggles upload dropzone to swap the template file -->
                    <button
                      v-if="documentTemplate.generated_count > 0"
                      class="btn btn-sm btn-outline-primary"
                      @click="showReplaceWordUpload = !showReplaceWordUpload"
                    >
                      <i class="mdi mdi-swap-horizontal me-1"></i>{{ t('documentTemplateDetail.replace_template') }}
                    </button>
                  </div>
                </div>
                <!-- Replacement upload dropzone (shown on demand via toggle button) -->
                <div v-if="showReplaceWordUpload" class="mt-3">
                  <FileListComponent
                    :single-file="true"
                    accepted-files=".docx"
                    :show-file-preview="false"
                    :fileable="'App\\Models\\DocumentTemplate'"
                    :fileable-id="documentTemplate.id"
                  />
                </div>
              </div>
            </div>

            <div v-show="templateType === 'pdf'" class="card-body">
              <!-- Warning: existing generated documents will need to be regenerated after template change -->
              <div v-if="documentTemplate.generated_count > 0" class="alert alert-warning mb-3">
                <i class="mdi mdi-alert-circle-outline me-1"></i>
                {{ t('documentTemplateDetail.template_upload_with_docs_warning') }}
              </div>

              <!-- Upload area: shown when no PDF template is attached yet -->
              <FileListComponent
                v-if="!documentTemplate.template_name || documentTemplate.mime_type !== 'application/pdf'"
                :single-file="true"
                accepted-files=".pdf"
                :show-file-preview="false"
                :fileable="'App\\Models\\DocumentTemplate'"
                :fileable-id="documentTemplate.id"
                :keep-annotations="false"
              />

              <!-- File info: shown when a PDF template is already attached -->
              <div v-else>
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <span :class="fileIcons.getClassWithColor(documentTemplate.template_name)" class="me-1"></span>
                    {{ documentTemplate.template_name }}
                  </div>
                  <div class="d-flex gap-3 align-items-center">
                    <router-link :to="'/documents/templates/' + documentTemplate.id + '/preview'" class="mdi mdi-file-key text-primary cursor-pointer fs-4"></router-link>
                    <span @click="downloadTemplate('pdf')" class="mdi mdi-download text-primary cursor-pointer fs-4"></span>
                    <!-- Delete only allowed when no documents have been generated yet -->
                    <span v-if="documentTemplate.generated_count < 1" @click="removeTemplate()" class="mdi mdi-trash-can text-danger cursor-pointer fs-4"></span>
                    <span v-else class="mdi mdi-trash-can text-danger opacity-50 fs-4"></span>
                    <!-- Replace button: toggles upload dropzone to swap the template file -->
                    <button
                      v-if="documentTemplate.generated_count > 0"
                      class="btn btn-sm btn-outline-primary"
                      @click="showReplacePdfUpload = !showReplacePdfUpload"
                    >
                      <i class="mdi mdi-swap-horizontal me-1"></i>{{ t('documentTemplateDetail.replace_template') }}
                    </button>
                  </div>
                </div>
                <!-- Replacement upload dropzone (shown on demand via toggle button) -->
                <div v-if="showReplacePdfUpload" class="mt-3">
                  <!-- Checkbox: keep existing annotation layer (form fields, signatures) from the old PDF -->
                  <div class="form-check mb-3">
                    <input class="form-check-input" type="checkbox" id="keepPdfAnnotations" v-model="keepPdfAnnotations">
                    <label class="form-check-label" for="keepPdfAnnotations">
                      {{ t('documentTemplateDetail.keep_pdf_annotations') }}
                    </label>
                  </div>
                  <FileListComponent
                    :single-file="true"
                    accepted-files=".pdf"
                    :show-file-preview="false"
                    :fileable="'App\\Models\\DocumentTemplate'"
                    :fileable-id="documentTemplate.id"
                    :keep-annotations="keepPdfAnnotations"
                  />
                </div>
              </div>
            </div>

            <div v-show="templateType === 'html'" class="card-body min-vh-25">
              <!-- Warning: existing generated documents will need to be regenerated after template change -->
              <div v-if="documentTemplate.generated_count > 0" class="alert alert-warning mb-3">
                <i class="mdi mdi-alert-circle-outline me-1"></i>
                {{ t('documentTemplateDetail.template_upload_with_docs_warning') }}
              </div>
              <QuillEditor style="max-height: 200px; height: auto;" ref="quill" :options="quillOptions"/>
              <div class="d-flex justify-content-end align-items-center mt-2">
                <Button :label="t('documentTemplateDetail.save')" icon="mdi-content-save" @click="saveDocumentTemplateHtml"/>
              </div>
            </div>
          </div>
          </div>
        </div>
      </div>
    </div>

    <GeneratedDocuments v-if="Object.keys(documentTemplate).length" :document-template="documentTemplate"/>
  </div>
</template>

<style scoped>
</style>
